import { useEffect, useState } from 'react';
import api from '../lib/api';
import { useAuthStore } from '../store/authStore';
import { Megaphone, Plus, Copy, Trash2, Pause, Play } from 'lucide-react';
import Modal from '../components/Modal';

const PORTALS = ['ZAP', 'VIVAREAL', 'OLX', 'IMOVELWEB', 'CHAVESNAMAO', 'SITE', 'META'] as const;
const STATUSES = ['DRAFT', 'ACTIVE', 'PAUSED', 'SOLD', 'REMOVED'] as const;

const statusColor: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-700',
  PAUSED: 'bg-yellow-100 text-yellow-700',
  DRAFT: 'bg-gray-100 text-gray-600',
  SOLD: 'bg-blue-100 text-blue-700',
  REMOVED: 'bg-red-100 text-red-700',
};

function feedBase(): string {
  const raw = (import.meta.env.VITE_API_URL || '/api').trim();
  return /^https?:\/\//i.test(raw) ? raw.replace(/\/+$/, '') : `${window.location.origin}/api`;
}

export default function ListingsPage() {
  const user = useAuthStore((s) => s.user) as any;
  const tenantId = user?.tenantId || '<tenantId>';
  const [publications, setPublications] = useState<any[]>([]);
  const [summary, setSummary] = useState<any[]>([]);
  const [portalFilter, setPortalFilter] = useState('');
  const [showPublish, setShowPublish] = useState(false);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params: any = {};
      if (portalFilter) params.portal = portalFilter;
      const [pubs, sum] = await Promise.all([
        api.get('/listings', { params }),
        api.get('/listings/summary'),
      ]);
      setPublications(pubs.data || []);
      setSummary(sum.data || []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalFilter]);

  async function changeStatus(id: string, status: string) {
    await api.patch(`/listings/${id}`, { status });
    load();
  }
  async function remove(id: string) {
    if (!confirm('Remover esta publicação?')) return;
    await api.delete(`/listings/${id}`);
    load();
  }

  const activeCount = summary
    .filter((s) => s.status === 'ACTIVE')
    .reduce((acc, s) => acc + s.count, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold dark:text-dark-text-primary flex items-center gap-2">
            <Megaphone className="w-6 h-6" /> Anúncios
          </h1>
          <p className="text-sm text-gray-500 dark:text-dark-text-secondary">
            Publique imóveis nos portais e acompanhe o status. {activeCount} anúncio(s) ativo(s).
          </p>
        </div>
        <button
          onClick={() => setShowPublish(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg"
        >
          <Plus className="w-4 h-4" /> Publicar imóvel
        </button>
      </div>

      {/* Feeds dos portais */}
      <div className="bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg p-4">
        <p className="text-sm font-medium mb-2 dark:text-dark-text-primary">
          URLs de feed (cadastre no painel de anunciante do portal)
        </p>
        <div className="grid md:grid-cols-2 gap-2">
          {['ZAP', 'VIVAREAL', 'OLX', 'SITE'].map((p) => {
            const url = `${feedBase()}/listings/feed/${tenantId}.xml?portal=${p}`;
            return (
              <div key={p} className="flex items-center gap-2 text-xs">
                <span className="w-20 font-medium dark:text-dark-text-primary">{p}</span>
                <code className="flex-1 truncate bg-gray-50 dark:bg-dark-bg-tertiary px-2 py-1 rounded dark:text-dark-text-secondary">
                  {url}
                </code>
                <button
                  onClick={() => navigator.clipboard?.writeText(url)}
                  className="p-1 text-gray-400 hover:text-gray-700"
                  title="Copiar"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filtro por portal */}
      <div className="flex gap-1 flex-wrap">
        <button
          onClick={() => setPortalFilter('')}
          className={`px-3 py-1 text-sm rounded ${portalFilter === '' ? 'bg-primary-600 text-white' : 'bg-gray-100 dark:bg-dark-bg-tertiary dark:text-dark-text-secondary'}`}
        >
          Todos
        </button>
        {PORTALS.map((p) => (
          <button
            key={p}
            onClick={() => setPortalFilter(p)}
            className={`px-3 py-1 text-sm rounded ${portalFilter === p ? 'bg-primary-600 text-white' : 'bg-gray-100 dark:bg-dark-bg-tertiary dark:text-dark-text-secondary'}`}
          >
            {p}
          </button>
        ))}
      </div>

      {/* Tabela */}
      <div className="bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-dark-bg-tertiary text-left">
            <tr className="dark:text-dark-text-secondary">
              <th className="px-4 py-2">Imóvel</th>
              <th className="px-4 py-2">Portal</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Destaque</th>
              <th className="px-4 py-2 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                  Carregando...
                </td>
              </tr>
            )}
            {!loading && publications.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                  Nenhuma publicação. Clique em "Publicar imóvel".
                </td>
              </tr>
            )}
            {publications.map((p) => (
              <tr key={p.id} className="border-t border-gray-100 dark:border-dark-border dark:text-dark-text-primary">
                <td className="px-4 py-2">
                  <span className="font-medium">{p.property?.code}</span>{' '}
                  <span className="text-gray-500 dark:text-dark-text-secondary">{p.property?.title}</span>
                </td>
                <td className="px-4 py-2">{p.portal}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${statusColor[p.status]}`}>
                    {p.status}
                  </span>
                </td>
                <td className="px-4 py-2">{p.featured ? '⭐' : '—'}</td>
                <td className="px-4 py-2 text-right space-x-1">
                  {p.status === 'ACTIVE' ? (
                    <button
                      onClick={() => changeStatus(p.id, 'PAUSED')}
                      className="p-1 text-yellow-600"
                      title="Pausar"
                    >
                      <Pause className="w-4 h-4 inline" />
                    </button>
                  ) : (
                    <button
                      onClick={() => changeStatus(p.id, 'ACTIVE')}
                      className="p-1 text-green-600"
                      title="Ativar"
                    >
                      <Play className="w-4 h-4 inline" />
                    </button>
                  )}
                  <button onClick={() => remove(p.id)} className="p-1 text-red-600" title="Remover">
                    <Trash2 className="w-4 h-4 inline" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showPublish && (
        <PublishModal
          onClose={() => setShowPublish(false)}
          onDone={() => {
            setShowPublish(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function PublishModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [properties, setProperties] = useState<any[]>([]);
  const [propertyId, setPropertyId] = useState('');
  const [portals, setPortals] = useState<string[]>(['ZAP', 'VIVAREAL', 'OLX']);
  const [featured, setFeatured] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get('/properties', { params: { limit: 1000 } })
      .then(({ data }) => {
        setProperties(Array.isArray(data) ? data : data?.data || data?.items || []);
      })
      .catch(() => setProperties([]));
  }, []);

  function togglePortal(p: string) {
    setPortals((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  }

  async function publish() {
    if (!propertyId || portals.length === 0) {
      alert('Selecione um imóvel e ao menos um portal.');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/listings/${propertyId}/publish`, { portals, featured });
      onDone();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Falha ao publicar.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title="Publicar imóvel nos portais" size="md">
      <div className="space-y-4">
        <label className="block text-sm dark:text-dark-text-primary">
          Imóvel
          <select
            value={propertyId}
            onChange={(e) => setPropertyId(e.target.value)}
            className="mt-1 w-full border border-gray-200 dark:border-dark-border rounded-lg px-2 py-2 dark:bg-dark-bg-tertiary dark:text-dark-text-primary"
          >
            <option value="">Selecione...</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} — {p.title}
              </option>
            ))}
          </select>
        </label>

        <div className="text-sm dark:text-dark-text-primary">
          Portais
          <div className="mt-1 flex flex-wrap gap-2">
            {PORTALS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => togglePortal(p)}
                className={`px-3 py-1 rounded-lg border text-sm ${portals.includes(p) ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-200 dark:border-dark-border dark:text-dark-text-secondary'}`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm dark:text-dark-text-primary">
          <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} />
          Marcar como destaque
        </label>

        <button
          onClick={publish}
          disabled={saving}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
        >
          {saving ? 'Publicando...' : 'Publicar'}
        </button>
      </div>
    </Modal>
  );
}
